import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";

import {
  getAppointmentEligibilityList,
  updateAppointmentEligibilityStatus,
} from "apiHandler/actions";
import { useUserData } from "components/customHooks/userDataContext";
import { EligibilityHeader } from "components/eligibility";
import dayjs from "dayjs";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { DataGridCustomPagination } from "components/helpers";
import { columns, editableFields } from "./fields";
import useSnackbar from "components/customHooks/useSnackbar";

const Eligibility = () => {
  const userData = useUserData();
  const [dos, setDos] = useState(dayjs());
  const [edit, setEdit] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const token = userData?.access_token;
  const { CustomSnackbar, setSnackbarMessage } = useSnackbar();

  useEffect(() => {
    getList();
  }, [dos]);

  const getList = async () => {
    setLoading(true);
    try {
      const payload = {
        practice_id: `${userData.selected_practice}`,
        dos: dayjs(dos).format("MM/DD/YYYY"),
      };
      const res = await getAppointmentEligibilityList(payload, token);
      if (res) {
        setData(res);
      }
    } catch (error) {
      console.log("error getlist: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (row) => {
    setEdit(row);
    setOpen(true);
  };

  const handleSave = async () => {
    setOpen(false);
    setLoading(true);
    try {
      const payload = {};

      if (edit?.encounter_details_id)
        payload.encounter_details_id = edit.encounter_details_id;
      if (edit?.encounter_details_eligibility_verification_id)
        payload.encounter_details_eligibility_verification_id =
          edit.encounter_details_eligibility_verification_id;
      if (edit?.payer_id) payload.payer_id = edit.payer_id;
      if (edit?.payer_name) payload.payer_name = edit.payer_name;
      if (edit?.verification_status)
        payload.verification_status = edit.verification_status;
      if (edit?.copay_inn) payload.copay_inn = edit.copay_inn;
      if (edit?.copay_oon) payload.copay_oon = edit.copay_oon;
      if (edit?.ded_inn) payload.ded_inn = edit.ded_inn;
      if (edit?.ded_remaining_inn)
        payload.ded_remaining_inn = edit.ded_remaining_inn;
      if (edit?.ded_oon) payload.ded_oon = edit.ded_oon;
      if (edit?.ded_remaining_oon)
        payload.ded_remaining_oon = edit.ded_remaining_oon;
      if (edit?.coins_ind_inn) payload.coins_ind_inn = edit.coins_ind_inn;
      if (edit?.coins_fam_inn) payload.coins_fam_inn = edit.coins_fam_inn;
      if (edit?.pcp_name) payload.pcp_name = edit.pcp_name;
      if (edit?.notes) payload.notes = edit.notes;

      // Include practice_id
      payload.practice_id = `${userData.selected_practice}`;
      const res = await updateAppointmentEligibilityStatus(payload, token);

      if (res && res.status === "Success") {
        setSnackbarMessage("Entry Updated Successfully");
        getList();
      }
    } catch (error) {
      setSnackbarMessage("Something Went Wrong. Please Try Again.");
      console.log("error handleSave: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEdit({ ...edit, [name]: value });
  };

  return (
    <Box>
      <CustomSnackbar />
      <EligibilityHeader dos={dos} setDos={setDos} />
      <DataGrid
        pageSize={25}
        density="compact"
        rows={data}
        columns={columns(handleEditClick)}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        pagination
        slots={{
          pagination: DataGridCustomPagination,
          toolbar: GridToolbar,
        }}
        getRowHeight={() => "auto"}
        getRowId={(row) => row.patient_id}
      />
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Edit Eligibility</DialogTitle>
        <DialogContent>
          {editableFields.map(({ label, name }) => (
            <TextField
              key={name}
              label={label}
              name={name}
              value={edit?.[name] || ""}
              fullWidth
              margin="dense"
              onChange={handleChange}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Eligibility;
